import * as Sentry from '@sentry/nuxt'

/**
 * Persists *which device the user picked*, not the device list itself — `MediaDeviceInfo[]`
 * always comes fresh from `enumerateDevices()`/`Room.getLocalDevices()` (see `deviceController.ts`),
 * never from storage. `deviceId` alone isn't a reliable enough key to survive a restart (some
 * platforms reassign it across sessions), so each preference also carries `groupId` and `label` as
 * fallback match keys — see `resolveDevicePreference` below.
 */
export interface StoredDevicePreference {
  kind: MediaDeviceKind
  deviceId?: string
  groupId?: string
  label?: string
  lastUsedAt: number
}

export type DevicePreferenceSlot = 'microphone' | 'camera' | 'speaker'

interface DevicePreferences {
  microphone?: StoredDevicePreference
  camera?: StoredDevicePreference
  speaker?: StoredDevicePreference
}

const STORAGE_KEY = 'iopta:device-preferences'

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readAll(): DevicePreferences {
  if (!canUseLocalStorage()) return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed as DevicePreferences : {}
  } catch (e) {
    console.warn('[DevicePreferences] Failed to read stored preferences:', e)
    Sentry.captureException(e, { level: 'warning', extra: { action: 'read_device_preferences' } })
    return {}
  }
}

function writeAll(preferences: DevicePreferences) {
  if (!canUseLocalStorage()) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
  } catch (e) {
    console.warn('[DevicePreferences] Failed to save preferences:', e)
    Sentry.captureException(e, { level: 'warning', extra: { action: 'save_device_preferences' } })
  }
}

export function getDevicePreference(slot: DevicePreferenceSlot): StoredDevicePreference | undefined {
  return readAll()[slot]
}

/** Call once a device switch has actually succeeded — never speculatively. */
export function saveDevicePreference(slot: DevicePreferenceSlot, device: Pick<MediaDeviceInfo, 'kind' | 'deviceId' | 'groupId' | 'label'>) {
  const preferences = readAll()
  preferences[slot] = {
    kind: device.kind,
    deviceId: device.deviceId,
    groupId: device.groupId || undefined,
    label: device.label || undefined,
    lastUsedAt: Date.now(),
  }
  writeAll(preferences)
}

/** The user picked «Как в системе»: forget the device, so nothing restores it over that choice. */
export function clearDevicePreference(slot: DevicePreferenceSlot) {
  const preferences = readAll()
  if (!preferences[slot]) return
  delete preferences[slot]
  writeAll(preferences)
}

/**
 * The devices a picker offers next to «Как в системе»: not the browser's own `default` alias, and
 * not the blank placeholder WebKit lists before mic/camera access (empty `deviceId`) — picking that
 * one stored an id no later list has, so the select went blank after «Обновить».
 */
export function selectableDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter(d => d.deviceId !== '' && d.deviceId !== 'default')
}

/**
 * Matches a stored preference against the *current* device list, in priority order:
 * exact `deviceId` -> same `groupId` (survives a deviceId reshuffle for the same physical
 * device) -> same `label` (survives a groupId change, e.g. a different USB port) -> nothing,
 * meaning the caller should fall back to the system default. Never invents a match against an
 * empty/placeholder label so an unlabeled pre-permission list can't accidentally "match" everything.
 */
export function resolveDevicePreference(
  preference: StoredDevicePreference | undefined,
  devices: MediaDeviceInfo[],
): MediaDeviceInfo | undefined {
  if (!preference) return undefined

  if (preference.deviceId) {
    const byId = devices.find(d => d.deviceId === preference.deviceId)
    if (byId) return byId
  }
  if (preference.groupId) {
    const byGroup = devices.find(d => d.groupId === preference.groupId)
    if (byGroup) return byGroup
  }
  if (preference.label) {
    const byLabel = devices.find(d => d.label !== '' && d.label === preference.label)
    if (byLabel) return byLabel
  }
  return undefined
}
